// audio-montaggio.ts — monta la traccia audio della clip seguendo la scaletta (funzione pura, provata con node:test).
// Conto e secondi normali: l'audio vero. Tratto accelerato: silenzio. Rallentatore: l'audio vero allungato (più
// grave, effetto «moviola»). Timbro: un colpo sordo sintetico. Finale: due note brevi. Niente campioni esterni.
import type { Montaggio } from "./montaggio.ts";

/** Estrae l'audio della sorgente tra `da` e `a` ms, alla frequenza dell'archivio. */
export type Estrattore = (da: number, a: number) => Float32Array;

const DISSOLVENZA_S = 0.015;

function dissolvi(buf: Float32Array, da: number, n: number, frequenza: number): void {
  const d = Math.min(Math.floor(n / 2), Math.round(DISSOLVENZA_S * frequenza));
  for (let i = 0; i < d; i++) {
    const k = i / d;
    buf[da + i] = (buf[da + i] ?? 0) * k;
    buf[da + n - 1 - i] = (buf[da + n - 1 - i] ?? 0) * k;
  }
}

/** Ricampiona (interpolazione lineare) `sorgente` su `n` campioni; con `n` maggiore il suono si abbassa. */
function stira(sorgente: Float32Array, n: number): Float32Array {
  const out = new Float32Array(n);
  if (!sorgente.length || !n) return out;
  const passo = sorgente.length / n;
  for (let i = 0; i < n; i++) {
    const x = i * passo;
    const i0 = Math.floor(x);
    const f = x - i0;
    const a = sorgente[i0] ?? 0;
    const b = sorgente[Math.min(sorgente.length - 1, i0 + 1)] ?? a;
    out[i] = a + (b - a) * f;
  }
  return out;
}

export function montaAudio(m: Montaggio, estrai: Estrattore | null, frequenza: number): Float32Array {
  const totale = Math.round((m.durataMs / 1000) * frequenza);
  const out = new Float32Array(totale);
  for (const s of m.segmenti) {
    const da = Math.round((s.inizioClipMs / 1000) * frequenza);
    const n = Math.min(totale - da, Math.round((s.durataMs / 1000) * frequenza));
    if (n <= 0) continue;
    if ((s.tipo === "conto" || s.tipo === "normale" || s.tipo === "rallentatore") && estrai) {
      const src = estrai(s.daMs, s.aMs);
      // Normale: stessa durata (ricampionato solo per la frequenza). Rallentatore: allungato.
      const pezzo = stira(src, n);
      out.set(pezzo.subarray(0, n), da);
      dissolvi(out, da, n, frequenza);
    } else if (s.tipo === "timbro") {
      // Colpo sordo: sinusoide a 110 Hz che scende e si spegne in 0,25 s.
      const durata = Math.min(n, Math.round(0.25 * frequenza));
      let fase = 0;
      for (let i = 0; i < durata; i++) {
        const t = i / frequenza;
        fase += (2 * Math.PI * (110 - 50 * (t / 0.25))) / frequenza;
        out[da + i] = Math.sin(fase) * 0.55 * Math.exp(-t * 14);
      }
    } else if (s.tipo === "finale") {
      // Due note brevi (la, mi sopra), discrete.
      for (const [inizio, hz] of [[0.05, 660], [0.22, 880]] as const) {
        const i0 = da + Math.round(inizio * frequenza);
        const durata = Math.round(0.28 * frequenza);
        for (let i = 0; i < durata && i0 + i < da + n; i++) {
          const t = i / frequenza;
          out[i0 + i] = (out[i0 + i] ?? 0) + Math.sin(2 * Math.PI * hz * t) * 0.18 * Math.exp(-t * 9) * Math.min(1, t * 200);
        }
      }
    }
  }
  // Niente distorsione: se il picco supera 0,95 si abbassa tutto.
  let picco = 0;
  for (let i = 0; i < out.length; i++) picco = Math.max(picco, Math.abs(out[i] ?? 0));
  if (picco > 0.95) for (let i = 0; i < out.length; i++) out[i] = (out[i] ?? 0) * (0.95 / picco);
  return out;
}
